/**
 * Etapa 5: Confirmação final e salvamento da lista
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  CheckCircle2,
  FileSpreadsheet,
  Clock,
  Hash,
  Users,
  Variable,
  PartyPopper
} from 'lucide-react';

import { CSVParseResult, extractVariableColumns } from '@/lib/csv-utils';
import { ImportResult } from '@/hooks/useListImport';

// =====================================================
// TIPOS
// =====================================================

interface SaveListStepProps {
  importResult: ImportResult | null;
  listName: string;
  csvData: CSVParseResult | null;
  onComplete: () => void;
}

// =====================================================
// COMPONENTE
// =====================================================

export default function SaveListStep({
  importResult,
  listName,
  csvData,
  onComplete
}: SaveListStepProps) {
  if (!importResult || !csvData) {
    return null;
  }
  
  const variableColumns = extractVariableColumns(csvData.headers);
  const totalContacts = importResult.imported + importResult.updated + importResult.skipped;
  
  // Calcular data de expiração
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 3);
  
  return (
    <div 
      className="space-y-6 w-full" 
      style={{ 
        maxWidth: '100%', 
        overflow: 'hidden', 
        contain: 'inline-size' 
      }}
    >
      {/* Sucesso! */}
      <div className="text-center py-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
          <PartyPopper className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-green-600">Lista criada com sucesso!</h2>
        <p className="text-muted-foreground mt-2">
          Sua lista está pronta para ser usada em campanhas
        </p>
      </div>
      
      {/* Detalhes da lista */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            {listName}
          </CardTitle>
          <CardDescription>
            Código: <code className="bg-muted px-2 py-1 rounded">{importResult.hashCode}</code>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Estatísticas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <Users className="h-5 w-5 mx-auto text-primary mb-1" />
              <p className="text-xl font-bold">{totalContacts}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <CheckCircle2 className="h-5 w-5 mx-auto text-green-500 mb-1" />
              <p className="text-xl font-bold text-green-600">{importResult.imported}</p>
              <p className="text-xs text-muted-foreground">Novos</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <CheckCircle2 className="h-5 w-5 mx-auto text-blue-500 mb-1" />
              <p className="text-xl font-bold text-blue-600">{importResult.updated}</p>
              <p className="text-xs text-muted-foreground">Atualizados</p>
            </div>
            <div className="text-center p-3 bg-orange-50 rounded-lg">
              <Clock className="h-5 w-5 mx-auto text-orange-500 mb-1" />
              <p className="text-xl font-bold text-orange-600">{importResult.skipped}</p>
              <p className="text-xs text-muted-foreground">Ignorados</p>
            </div>
          </div>
          
          {/* Variáveis - com scroll horizontal para muitas colunas */}
          {variableColumns.length > 0 && (
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 mb-2">
                <Variable className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Variáveis disponíveis:</span>
              </div>
              
              {/* Container com scroll horizontal para muitas variáveis */}
              <div 
                className="relative overflow-hidden" 
                style={{ maxWidth: '100%', width: '100%' }}
              >
                {/* Gradiente de fade à direita */}
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
                
                <div 
                  className="flex items-center gap-2 pb-2 pr-8 overflow-x-auto overflow-y-hidden scrollbar-thin"
                  style={{ 
                    WebkitOverflowScrolling: 'touch',
                    scrollBehavior: 'smooth'
                  }}
                >
                  <Badge variant="secondary" className="shrink-0">{'{{nome}}'}</Badge>
                  <Badge variant="secondary" className="shrink-0">{'{{telefone}}'}</Badge>
                  {variableColumns.map(col => (
                    <Badge key={col} variant="outline" className="shrink-0">{`{{${col}}}`}</Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Aviso de expiração */}
      <Alert>
        <Clock className="h-4 w-4" />
        <AlertDescription>
          O arquivo CSV temporário será excluído em{' '}
          <strong>{expiresAt.toLocaleDateString('pt-BR')}</strong>.
          Os metadados e variáveis da lista serão mantidos permanentemente.
        </AlertDescription>
      </Alert>
      
      {/* Próximos passos */}
      <Card className="bg-primary/5 border-primary/20">
        <CardHeader>
          <CardTitle className="text-lg">Próximos passos</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5" />
              <span>Acesse <strong>Campanhas</strong> e crie uma nova campanha</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5" />
              <span>Na etapa de destinatários, selecione <strong>"Listagem de arquivos"</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5" />
              <span>Escolha esta lista e configure as mensagens com as variáveis</span>
            </li>
          </ul>
        </CardContent>
      </Card>
      
      {/* Botão de finalizar */}
      <div className="flex justify-center">
        <Button onClick={onComplete} size="lg">
          <CheckCircle2 className="h-4 w-4 mr-2" />
          Concluir
        </Button>
      </div>
    </div>
  );
}
